import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Building2, Plus, Edit2, Trash2, CreditCard, Check, X as XIcon,
  LayoutDashboard, Upload, List, ClipboardCheck, FileBarChart, Shield, LogOut, Menu, X
} from "lucide-react";

export default function AccountsPage() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAccount, setEditingAccount] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({ name: "", bank_name: "", account_number: "", account_type: "business" });
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/accounts`, { headers, withCredentials: true });
      setAccounts(response.data);
    } catch (error) {
      toast.error("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async () => {
    if (!formData.name.trim()) {
      toast.error("Account name is required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/accounts`, formData, { headers, withCredentials: true });
      toast.success("Account added");
      setShowAddDialog(false);
      setFormData({ name: "", bank_name: "", account_number: "", account_type: "business" });
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add account");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAccount = async () => {
    if (!editingAccount || !formData.name.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/accounts/${editingAccount.account_id}`, formData, { headers, withCredentials: true });
      toast.success("Account updated");
      setEditingAccount(null);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update account");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!confirm("Are you sure you want to deactivate this account?")) return;
    try {
      await axios.delete(`${API}/accounts/${accountId}`, { headers, withCredentials: true });
      toast.success("Account deactivated");
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    }
  };

  const startEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      bank_name: account.bank_name || "",
      account_number: account.account_number || "",
      account_type: account.account_type || "business"
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavLink = ({ to, icon: Icon, children, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${active ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'}`}>
      <Icon className="w-5 h-5" />
      <span className="text-sm font-medium">{children}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#121214] border-r border-zinc-800 hidden lg:flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#1C1917]" />
            </div>
            <div>
              <h1 className="font-semibold text-white">BankFlow Control</h1>
              <p className="text-xs text-zinc-500">Financial Analysis</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
          <NavLink to="/upload" icon={Upload}>Upload Statements</NavLink>
          <NavLink to="/transactions" icon={List}>Transactions</NavLink>
          <NavLink to="/review" icon={ClipboardCheck}>Review Queue</NavLink>
          <NavLink to="/reports" icon={FileBarChart}>Reports</NavLink>
          <NavLink to="/accounts" icon={CreditCard} active>Bank Accounts</NavLink>
          {user?.role === 'admin' && <NavLink to="/admin" icon={Shield}>Admin Settings</NavLink>}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-medium text-white">{user?.name?.charAt(0) || 'U'}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#121214] border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1C1917]" />
            </div>
            <span className="font-semibold text-white">BankFlow</span>
          </Link>
          <Button variant="ghost" size="icon" className="text-zinc-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Bank Accounts</h1>
              <p className="text-zinc-400 text-sm mt-1">Manage your connected bank accounts</p>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]" data-testid="add-account-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Account
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121214] border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-white">Add Bank Account</DialogTitle>
                  <DialogDescription className="text-zinc-400">
                    Add a new bank account to track statements
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Account Name *</Label>
                    <Input
                      placeholder="e.g., Business Current Account"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-zinc-900/50 border-zinc-800 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Bank Name</Label>
                    <Input
                      placeholder="e.g., Barclays"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      className="bg-zinc-900/50 border-zinc-800 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Account Number (last 4 digits)</Label>
                    <Input
                      placeholder="e.g., ****1234"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      className="bg-zinc-900/50 border-zinc-800 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Account Type</Label>
                    <Select value={formData.account_type} onValueChange={(v) => setFormData({ ...formData, account_type: v })}>
                      <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121214] border-zinc-800">
                        <SelectItem value="business" className="text-white hover:bg-zinc-800">Business</SelectItem>
                        <SelectItem value="savings" className="text-white hover:bg-zinc-800">Savings</SelectItem>
                        <SelectItem value="personal" className="text-white hover:bg-zinc-800">Personal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                    Cancel
                  </Button>
                  <Button onClick={handleAddAccount} disabled={saving} className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]">
                    {saving ? <div className="w-4 h-4 border-2 border-[#1C1917] border-t-transparent rounded-full animate-spin" /> : "Add Account"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#FCD34D] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {accounts.map((account) => (
                <Card key={account.account_id} className={`bg-[#121214] border-zinc-800 ${!account.is_active ? 'opacity-50' : ''}`}>
                  <CardContent className="p-6">
                    {editingAccount?.account_id === account.account_id ? (
                      <div className="space-y-4">
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="bg-zinc-900/50 border-zinc-800 text-white"
                          placeholder="Account name"
                        />
                        <Input
                          value={formData.bank_name}
                          onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                          className="bg-zinc-900/50 border-zinc-800 text-white"
                          placeholder="Bank name"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleUpdateAccount} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingAccount(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                            <XIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 rounded-lg bg-[#FCD34D]/10 flex items-center justify-center">
                            <CreditCard className="w-6 h-6 text-[#FCD34D]" />
                          </div>
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-white hover:bg-zinc-800" onClick={() => startEdit(account)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {user?.role === 'admin' && (
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-500 hover:text-red-500 hover:bg-zinc-800" onClick={() => handleDeleteAccount(account.account_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-1">{account.name}</h3>
                        <p className="text-sm text-zinc-500">
                          {account.bank_name || 'No bank specified'}
                          {account.account_number && ` · ${account.account_number}`}
                        </p>
                        <div className="mt-4 flex items-center justify-between">
                          <span className={`text-xs px-2 py-1 rounded-full ${account.is_active ? 'bg-emerald-500/10 text-emerald-500' : 'bg-zinc-500/10 text-zinc-500'}`}>
                            {account.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="text-xs text-zinc-500 capitalize">{account.account_type}</span>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
